import React, { useState, useRef, useLayoutEffect, useCallback } from "react";
import classnames from "classnames";
import Icon from "../icons/Icon";
import { useHasWidth } from "./useHasWidth";
import "./ToolTip.scss";

const ToolTipContent = ({
  content,
  show,
  closeTooltip,
  mainRef,
  nowrap,
  contentId,
  triggerId,
  position,
  handleMouseEnter,
  handleMouseLeave,
  manuallyClose,
  width,
  flexiblePosition,
  showByDefault,
  variant
}) => {
  const [isReady, setReady] = useState();

  /*
   * also, if the tip is shown by default, then we have to temporarily set the width to big...
   * so we can accurately measure the inner content so it positions correctly
   */
  const defaultInitialWidth =
    (showByDefault && !width && { styles: { width: "1000px" } }) || {};

  /**
   * if user-defined width, set the pos default to use it
   * .. so the getBoundingClientRect below will do calculations based on the custom width
   */
  const [pos, setPos] = useState(width ? { width } : defaultInitialWidth);

  const contentRef = useRef(null);
  const triggerIsRendered = useHasWidth(mainRef);
  const contentIsRendered = useHasWidth(contentRef);

  /**
   * Do all of the calculations and update the 'pos' state with the styles for the tooltip position
   */
  const setPosition = useCallback(() => {
    const triggerRect = mainRef?.current
      ? mainRef.current.getBoundingClientRect()
      : null;

    const contentRect = contentRef?.current
      ? contentRef.current.getBoundingClientRect()
      : null;

    // if the trigger or content element isn't available, then just stop
    if (!triggerRect || !contentRect) return false;

    /**
     * User-defined width:
     * if the user set the width manually, then we need to hard-set the width
     * calculations to include the padding and whatnot to center the content correctly
     */
    const computedStyle = getComputedStyle(contentRef.current);
    const customWidth = width
      ? width +
        [
          "margin-left",
          "margin-right",
          "border-left",
          "border-right",
          "padding-left",
          "padding-right"
        ]
          .map(k => parseFloat(computedStyle.getPropertyValue(k)))
          .reduce((prev, cur) => prev + cur)
      : undefined;

    const contentHeight = contentRect?.height;
    const contentWidth = customWidth || contentRect?.width;

    const centeredHoriztonal = (triggerRect.width - contentWidth) / 2;
    const centeredVertical = (triggerRect.height - contentHeight) / 2;

    const availableSpace = {
      above: triggerRect?.top || 0,
      below: window.innerHeight - (triggerRect?.bottom || 0),
      left: triggerRect?.left || 0,
      right: window.innerWidth - (triggerRect?.right || 0)
    };

    /**
     * Flip the position if there's not enough space to fit it
     * "flexiblePosition" is set to 'true' by default
     * ... but the user can set it to false to force the position to be the set position
     */
    const _pos = flexiblePosition
      ? position === "top" && availableSpace.above < contentHeight
        ? "bottom"
        : position === "bottom" && availableSpace.below < contentHeight
        ? "top"
        : position === "left" && availableSpace.left < contentWidth
        ? "right"
        : position === "right" && availableSpace.right < contentWidth
        ? "left"
        : position
      : position;

    const positionDetails = {
      top: {
        top: `-${contentRect.height}px`,
        left: `${centeredHoriztonal}px`
      },
      bottom: {
        top: `${triggerRect.height + 4}px`,
        left: `${centeredHoriztonal}px`
      },
      left: {
        top: `${centeredVertical}px`,
        left: `-${contentWidth}px`
      },
      right: {
        top: `${centeredVertical}px`,
        right: `-${contentWidth}px`
      }
    };

    // DEBUGING TOOLS
    // console.log({
    //   width,
    //   triggerId,
    //   triggerRect,
    //   contentRect,
    //   contentHeight,
    //   contentWidth,
    //   centeredHoriztonal,
    //   centeredVertical,
    //   availableSpace,
    //   _pos,
    //   positionDetails
    // });

    setPos({ ...pos, usedPosition: _pos, styles: positionDetails[_pos] });
  }, [flexiblePosition, mainRef, pos, position, width]);

  /**
   * If we're showing this tip at render... and if the user didn't define a width
   * we have to do some fudgery to figure out the width before showing the tip
   *
   * Using this effect to detect when we're ready to continue
   * ... and to trigger the opening of the showByDefault tips
   */
  useLayoutEffect(() => {
    if (!isReady && contentIsRendered) {
      if (showByDefault && !width) {
        const innerWidth = contentRef.current.querySelector(
          `#${contentId}__inner`
        ).offsetWidth;
        setPos({ width: innerWidth });
        setReady(true);
      } else {
        setReady(true);
      }
    }
  }, [contentId, contentIsRendered, isReady, showByDefault, width]);

  useLayoutEffect(() => {
    // set the position of the content after the component renders
    if (triggerIsRendered && contentIsRendered && show && isReady)
      setPosition();
    // excluding setPosition here because it's throwing a loop error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerIsRendered, contentIsRendered, show, isReady]);

  return (
    <span
      aria-hidden={!show}
      id={contentId}
      role="tooltip"
      aria-labelledby={triggerId}
      ref={contentRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classnames(
        "uikit__tooltip__content",
        `uikit__tooltip__content__variant-${variant}`,
        `uikit__tooltip__content__position-${pos.usedPosition}`,
        {
          uikit__tooltip__content__nowrap: nowrap,
          [`uikit__tooltip__content__manual-close`]: manuallyClose,
          uikit__tooltip__content__ready: isReady,
          // if the tooltip is a hover, then render it and hide it in the dom
          uikit__tooltip__hidden: !show
        }
      )}
      style={pos.styles}
    >
      <span
        id={`${contentId}__inner`}
        className="uikit__tooltip__content__inner"
        style={pos.width && { width: `${pos.width}px` }}
      >
        {manuallyClose && (
          <button
            className="uikit__tooltip__content__close-icon"
            onClick={() => closeTooltip()}
          >
            <Icon icon="fasTimes" />
            <span className="uikit-sr-only">Close</span>
          </button>
        )}
        {content}
      </span>
    </span>
  );
};

export default ToolTipContent;
